import { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { useReportData } from '../hooks/useReportData';
import { ReportLineChart } from '../components/report/ReportLineChart';
import { ReportDrillDown } from '../components/report/ReportDrillDown';
import { ReportSection } from '../components/report/ReportSection';
import { KpiCard } from '../components/KpiCard';
import { fmtEur2, fmtJours, fmtNum } from '../components/report/formatters';
import { DELAY_COLORS } from '../lib/chart-theme';
import type { ReportParams } from '../types/widget';

interface PieRow { tranche: string; tri: number; regime: string; nb_factures: number; pct: number; montant_ht: number }
interface CourbeRow { mois: string; regime: string; nb_factures: number; delai_moyen_jours: number }

const RULE_TOOLTIP = 'Limite = fin de mois de (date facture + 30 j) − 7 j.\nDans les délais : validée avant cette limite.\nHors délais : validée après.';

export function DelaisTraitement({ filters }: { filters: ReportParams }) {
    const [drillFilter, setDrillFilter] = useState<{ tranche?: string; mois?: string } | null>(null);
    const [showRule, setShowRule] = useState(false);

    const pie = useReportData<PieRow>('/api/emis/delais-traitement/pie', filters);
    const courbe = useReportData<CourbeRow>('/api/emis/delais-traitement/courbe', filters);

    // KPIs
    const kpis = useMemo(() => {
        if (!pie.data.length) return null;
        const total = pie.data.reduce((s, r) => s + Number(r.nb_factures), 0);
        const dansDelais = pie.data.filter((r) => r.tranche.toLowerCase().includes('dans')).reduce((s, r) => s + Number(r.nb_factures), 0);
        const horsDelais = total - dansDelais;
        const pctDans = total > 0 ? (dansDelais / total) * 100 : 0;
        return { total, dansDelais, horsDelais, pctDans };
    }, [pie.data]);

    // Horizontal stacked bar: one bar per regime, segmented dans/hors délais
    const stackedBarData = useMemo(() => {
        const regimes = [...new Set(pie.data.map((r) => r.regime))].sort();
        const dansData: number[] = [];
        const horsData: number[] = [];

        for (const regime of regimes) {
            const rows = pie.data.filter((r) => r.regime === regime);
            const dans = rows.filter((r) => r.tranche.toLowerCase().includes('dans')).reduce((s, r) => s + Number(r.nb_factures), 0);
            const hors = rows.filter((r) => !r.tranche.toLowerCase().includes('dans')).reduce((s, r) => s + Number(r.nb_factures), 0);
            dansData.push(dans);
            horsData.push(hors);
        }

        return { regimes, dansData, horsData };
    }, [pie.data]);

    const stackedOption: EChartsOption = {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
        },
        legend: { bottom: 0 },
        grid: { left: '3%', right: '4%', bottom: '12%', top: '4%', containLabel: true },
        xAxis: { type: 'value' },
        yAxis: {
            type: 'category',
            data: stackedBarData.regimes,
            axisLabel: { fontSize: 12 },
        },
        series: [
            {
                name: 'Dans les délais',
                type: 'bar',
                stack: 'total',
                data: stackedBarData.dansData,
                itemStyle: { color: DELAY_COLORS.inTime },
                label: { show: true, position: 'inside', fontSize: 11, formatter: (p: any) => p.value > 0 ? fmtNum(p.value) : '' },
            },
            {
                name: 'Hors délais',
                type: 'bar',
                stack: 'total',
                data: stackedBarData.horsData,
                itemStyle: { color: DELAY_COLORS.late },
                label: { show: true, position: 'inside', fontSize: 11, formatter: (p: any) => p.value > 0 ? fmtNum(p.value) : '' },
            },
        ],
    };

    // Courbe
    const courbeData = useMemo(() => {
        const months = [...new Set(courbe.data.map((r) => r.mois))].sort();
        const regimes = [...new Set(courbe.data.map((r) => r.regime))];
        const byKey = new Map(courbe.data.map((r) => [`${r.regime}-${r.mois}`, r]));
        const series = regimes.map((regime) => ({
            name: regime,
            data: months.map((m) => byKey.get(`${regime}-${m}`)?.delai_moyen_jours ?? null),
            lineStyle: (regime === 'TOUS' ? 'dashed' : 'solid') as 'dashed' | 'solid',
        }));
        return { months, series };
    }, [courbe.data]);

    const drillParams = useMemo(() => ({ ...filters, ...drillFilter }), [filters, drillFilter]);

    const drillColumns = [
        { key: 'numero_facture', label: 'N° facture', filterable: true },
        { key: 'libelle', label: 'Libellé', filterable: true },
        { key: 'date_facture', label: 'Date facture' },
        { key: 'date_validation', label: 'Date validation' },
        { key: 'fournisseur', label: 'Fournisseur', filterable: true },
        { key: 'montant_ht', label: 'Montant HT', align: 'right' as const, format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'montant_ht', formatTotal: (v: number) => fmtEur2(v) },
        { key: 'delai_jours', label: 'Délai (j)', align: 'right' as const },
        { key: 'limite_30j_fdm', label: 'Limite 30j' },
        { key: 'seuil_7j', label: 'Seuil 7j' },
        { key: 'regime', label: 'Catégorie' },
        { key: 'tranche', label: 'Tranche', filterable: true },
        { key: 'date_validation_estimee', label: 'Estimée', format: (v: unknown) => v ? 'Oui' : 'Non' },
    ];

    // Tranches ordonnées pour navigation
    const orderedTranches = useMemo(
        () => [...new Set(pie.data.map((r) => r.tranche))].sort((a, b) => {
            const triA = pie.data.find((r) => r.tranche === a)?.tri ?? 0;
            const triB = pie.data.find((r) => r.tranche === b)?.tri ?? 0;
            return triA - triB;
        }),
        [pie.data],
    );

    const handleTrancheChange = (tranche: string) => {
        setDrillFilter({ tranche });
    };

    const onStackedClick = (params: any) => {
        const regime = stackedBarData.regimes[params.dataIndex];
        const tranche = params.seriesName === 'Dans les délais' ? 'Dans les délais' : 'Hors délais';
        setDrillFilter({ tranche });
    };

    return (
        <div className="space-y-6">
            {/* KPI Strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard label="Total factures" value={kpis ? fmtNum(kpis.total) : '—'} loading={pie.loading} />
                <KpiCard label="Dans les délais" value={kpis ? fmtNum(kpis.dansDelais) : '—'} loading={pie.loading} />
                <KpiCard label="Hors délais" value={kpis ? fmtNum(kpis.horsDelais) : '—'} loading={pie.loading} />
                <KpiCard label="Taux dans les délais" value={kpis ? `${kpis.pctDans.toFixed(1)} %` : '—'} loading={pie.loading} />
            </div>

            {/* Horizontal stacked bar — replaces 5 donuts */}
            <ReportSection title="Répartition dans/hors délais" loading={pie.loading} error={pie.error} meta={pie.meta}>
                <div className="flex items-center gap-2 mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Par catégorie</h4>
                    <div className="relative">
                        <button
                            className="text-gray-400 hover:text-gray-600 text-sm border border-gray-300 rounded-full w-5 h-5 flex items-center justify-center"
                            onMouseEnter={() => setShowRule(true)}
                            onMouseLeave={() => setShowRule(false)}
                            onClick={() => setShowRule(!showRule)}
                            aria-label="Règle de calcul"
                        >
                            i
                        </button>
                        {showRule && (
                            <div className="absolute z-10 left-6 top-0 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs text-gray-700">
                                <p className="font-medium mb-1">Règle de calcul</p>
                                <p>Limite = fin de mois de (date facture + 30 j) − 7 j.</p>
                                <p><strong>Dans les délais</strong> : facture validée avant cette limite.</p>
                                <p><strong>Hors délais</strong> : facture validée après.</p>
                            </div>
                        )}
                    </div>
                </div>
                <ReactECharts
                    option={stackedOption}
                    style={{ height: `${Math.max(200, stackedBarData.regimes.length * 60 + 60)}px` }}
                    showLoading={pie.loading}
                    onEvents={{ click: onStackedClick }}
                />
                {drillFilter?.tranche && (
                    <ReportDrillDown
                        title={`Factures traitées — ${drillFilter.tranche}`}
                        endpoint="/api/emis/delais-traitement/drill-down"
                        params={drillParams}
                        enabled={true}
                        onClose={() => setDrillFilter(null)}
                        columns={drillColumns}
                        tranches={orderedTranches}
                        currentTranche={drillFilter.tranche}
                        onTrancheChange={handleTrancheChange}
                    />
                )}
            </ReportSection>

            {/* Courbe */}
            <ReportSection title="Évolution mensuelle" loading={courbe.loading} error={courbe.error} meta={courbe.meta}>
                <ReportLineChart
                    title="Délai moyen de traitement par mois (par catégorie)"
                    categories={courbeData.months}
                    series={courbeData.series}
                    valueFormatter={fmtJours}
                    onPointClick={(_, mois) => setDrillFilter({ mois })}
                />
                {drillFilter?.mois && (
                    <ReportDrillDown
                        title={`Factures traitées — ${drillFilter.mois}`}
                        endpoint="/api/emis/delais-traitement/drill-down"
                        params={drillParams}
                        enabled={true}
                        onClose={() => setDrillFilter(null)}
                        columns={drillColumns}
                    />
                )}
            </ReportSection>
        </div>
    );
}
